import { Form, Formik } from "formik";
import React, { FC, useEffect, useState } from "react";
import Modal from "react-modal";
import UIFieldTypes, {findSettingDataColumnKey, isMultiSelectField} from "../../../constants/field-ui-type";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { DynamicFieldDescriptor } from "../../../models/DynamicFieldDescriptor";
import PresetCustomField from "../../../models/PresetCustomField";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { getPresetFields } from "../../../services/customFieldsService";
import { getPresetSettingById } from "../../../services/presetService";
import { showLoadItemError } from "../../../services/utilities/ServiceError";
import { useDialog } from "../../dialog/DialogContext";
import { SelectType } from "../../settingFields/SettingSelectField";
import WizardTwo from "../../wizard/wizardTwo";
import { WizardSummary } from "../wizardSummary";
import PresetDescription from "./presetDescription";
import PresetDetails from "./presetDetails";
import PresetMultiSelect from "./presetMultiSelect";
import { CUSTOM } from "../../../constants/used-by-status";
import PresetTriggeredBy from "./presetTriggeredBy";

interface PresetSettingModalProps {
    presetSettingId: string;
    systemProfileId: string;
    isOpen: boolean;
    title: string;
    onClose: () => void;
    presetTemplateType: SelectType;
    onSave: (formFields: any, createNew: boolean) => void;
}

const PresetSettingModal: FC<PresetSettingModalProps> = ({ presetSettingId, systemProfileId, isOpen, title, onClose, presetTemplateType, onSave }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(0);
    const initialValues: PresetCustomField = {
        id: 0,
        name: '',
        type: '',
        typeId: 0,
        usedBy: '',
        custom: {},
        sdist: "",
        settingIdToCopy: "",
        profileId: ""
    };
    const [currentItem, setCurrentItem] = useState<PresetCustomField>(initialValues);
    const initialSidebarSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.description),
            description: '',
            substeps: []
        }
    ];
    const [sidebarSteps, setSidebarSteps] = useState<WizardSideBarStep[]>(initialSidebarSteps);
    const dialog = useDialog();

    useEffect(() => {
        buildWizardData();
    }, [presetSettingId]);

    useEffect(() => {
        getSupportedFields();
    }, [currentItem]);

    const addEmptyFieldValues = (value: DynamicFieldDescriptor, newPreset): true => {
        const newEmptyCustomValueSelection = {
            isBoolean: false,
            value: '',
            id: value.id,
            elementSysname: value.systemName,
            settingDataId: '',
            settingDataColumnKey: findSettingDataColumnKey(value.fieldTypeId)
        };

        if (newPreset.custom) {
            if (newPreset.custom[value.systemName] === undefined) {
                newPreset.custom[value.systemName] = newEmptyCustomValueSelection;
            }
        }
        else {
            newPreset.custom = {
                [value.systemName]: newEmptyCustomValueSelection
            };
        }

        return true;
    };

    const setSideBarStepDescription = (index: number, newDescription: string) => {
        setSidebarSteps(prev => {
            const updated = [...prev];
            if (updated[index]) {
                updated[index].description = newDescription;
            }
            return updated;
        });
    }

    const onNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        setStep(step => step + 1);
    }

    const onBack = (e: React.MouseEvent<HTMLButtonElement>) => {
        setStep(step => {
            if (step - 1 === 0 && !!!presetSettingId) {
                // Reset to initial state
                setSidebarSteps(initialSidebarSteps);
                setContentPages(initialContentPages);
            }
            return step - 1;
        });
    }

    const initialContentPages: JSX.Element[] = [
        <PresetDescription
            key={"wizard-description"}
            systemProfileId={systemProfileId}
            presetTemplateType={presetTemplateType}
            onNext={onNext}
            setCurrentItem={setCurrentItem}
            setSideBarStepDescription={(newDescription) => setSideBarStepDescription(0, newDescription)}/>
    ];
    const [contentPages, setContentPages] = useState<JSX.Element[]>(initialContentPages);

    const handleClose = () => {
        setCurrentItem(initialValues);
        setSidebarSteps(initialSidebarSteps);
        setContentPages(initialContentPages);
        setStep(0);
        onClose();
    }

    const buildWizardData = async () => {
        try {
            if (presetSettingId) {
                const preset = await getPresetSettingById(presetSettingId, translate);
                const newSupportedFields = await getPresetFields(+(preset.typeId ?? 0));
                let newPreset: PresetCustomField = { ...preset };
                newSupportedFields.every((value) => addEmptyFieldValues(value, newPreset));
                setCurrentItem(newPreset);
                addExtraSteps(newSupportedFields, preset.sdist ?? "", preset.type ?? "");
                return;
            }

            setCurrentItem(initialValues);
        } catch (error) {
            showLoadItemError(error, dialog, translate, handleClose);
        }
    }

    const getSupportedFields = async () => {
        try {
            const typeTemplateId = parseInt(currentItem['typeTemplateId']);
            if (typeTemplateId) {
                const sDist: string = currentItem['sdist'] ?? "";
                const newSupportedFields = await getPresetFields(typeTemplateId);
                newSupportedFields.every((value) => addEmptyFieldValues(value, currentItem));
                addExtraSteps(newSupportedFields, sDist, currentItem['typeTemplateName']);
            }
        } catch (error) {
            showLoadItemError(error, dialog, translate, handleClose);
        }
    }

    const addExtraSteps = (newSupportedFields: DynamicFieldDescriptor[], sDist: string, typeTemplateName: string) => {
        const detailFields = newSupportedFields.filter(field => !isMultiSelectField(field.fieldTypeId));
        const multiSelectField = newSupportedFields.filter(field => isMultiSelectField(field.fieldTypeId));

        // Standard fields step
        contentPages.push(<PresetDetails key={"preset-details"} onNext={onNext} onBack={onBack} supportedFields={detailFields} setSideBarStepDescription={(newDescription) => setSideBarStepDescription(1, newDescription)} />)
        sidebarSteps.push({
            title: translate.byKey(TranslationKeys.details),
            description: '',
            substeps: []
        });

        // Multi select fields steps
        multiSelectField.forEach((field) => {
            const stepIndex = sidebarSteps.length;
            contentPages.push(<PresetMultiSelect key={"preset-multiselect-" + field.id} onNext={onNext} onBack={onBack} field={field} setSideBarStepDescription={(newDescription) => setSideBarStepDescription(stepIndex, newDescription)} />)
            sidebarSteps.push({
                title: field.displayName,
                description: '',
                substeps: []
            });
        });

        // If sDist is CUSTOM, add another step for custom assignment. Using double equals to allow case sensitivity.
        if (sDist == CUSTOM) {
            contentPages.push(<PresetTriggeredBy presetSettingId={presetSettingId} key={"preset-triggeredby"} onNext={onNext} onBack={onBack} setSideBarStepDescription={(newDescription) => setSideBarStepDescription(sidebarSteps.length - 2, newDescription)} />);
            sidebarSteps.push({
                title: translate.byKey(TranslationKeys.triggeredBy),
                description: '',
                substeps: []
            });
        }

        // Add summary step at the end
        contentPages.push(<WizardSummary key={"wizard-summary"} onBack={onBack} supportedFields={newSupportedFields} templateName={typeTemplateName} />);
        sidebarSteps.push({
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        });

        setContentPages([...contentPages]);
        setSidebarSteps([...sidebarSteps]);
    }

    const handleSubmit = (formFields) => {
        formFields.typeId = formFields.typeTemplateId || formFields.typeId;
        onSave(formFields, presetSettingId ? false : true);
        handleClose();
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none w-content">
        <Formik
            initialValues={currentItem}
            onSubmit={handleSubmit}
            enableReinitialize={true}>
            <Form>
                <WizardTwo
                    name={title}
                    step={step}
                    onClose={handleClose}
                    sidebarSteps={sidebarSteps}
                    stepContent={contentPages} />
            </Form>
        </Formik>
    </Modal>
}

export default PresetSettingModal;
